import {
  SESSION_KEYS,
  EvsesCompatibility,
  UI_FLOW_PREFERENCES,
} from "src/consts/session";
import {
  masterdataEvsesCompatibility,
  leadSmartChargingLightTransfer,
} from "src/api/smartChargingClients";
import {
  Box,
  List,
  Stack,
  Alert,
  Skeleton,
  ListItem,
  Snackbar,
  SnackbarCloseReason,
} from "@mui/material";
import Text from "src/components/Displays/Text";
import { Trans, useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { SMART_CHARGING_ROUTES } from "src/consts/Routes/SmartCharging";
import EvseListItem from "src/components/SmartCharging/Components/EvseListItem";

const Compatibility = () => {
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("translation");

  const transferId = searchParams.get("tid") || "";
  const salesPartnerUuid = searchParams.get("spid") || "";

  const [isLoading, setIsLoading] = useState(false);

  const [, setTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);
  const [, setCorrelationId] = useSessionStorage<string>(
    SESSION_KEYS.CORRELATION_ID,
  );
  const [, setUiFlowPreferences] = useSessionStorage<UI_FLOW_PREFERENCES>(
    SESSION_KEYS.UI_FLOW_PREFERENCES,
  );
  const [, setEvsesCompatibility] = useSessionStorage<EvsesCompatibility[]>(
    SESSION_KEYS.EVSES_COMPATIBILITY,
  );
  const [, setIsCompCheckFromSell] = useSessionStorage<string>(
    SESSION_KEYS.IS_COMP_CHECK_FROM_SELL,
  );

  const [manufacturersList, setManufacturersList] = useState<string[]>([]);

  const [getSessionStorageItem] = useSessionStorage<string>(
    SESSION_KEYS.TRANSFER_ID,
  );

  const initialCall = useCallback(async () => {
    setIsLoading(true);
    setIsCompCheckFromSell("true");

    try {
      const sessionTransferId = getSessionStorageItem();

      if (sessionTransferId && sessionTransferId === transferId) {
        const response = await masterdataEvsesCompatibility(transferId);
        setEvsesCompatibility(response);
        const filteredEvsesList = response.filter(
          (evse) => evse.compatibilityStatus === "PREMIUM",
        );
        const manufacturersList = filteredEvsesList.map(
          (evse) => evse.manufacturer,
        );
        const manufacturersListDistinct = manufacturersList.filter(
          (manufacturer, index, list) => list.indexOf(manufacturer) === index,
        );

        setTimeout(() => {
          setIsLoading(false);
          setManufacturersList(manufacturersListDistinct);
        }, 1000);

        return;
      }

      const response = await Promise.all([
        await leadSmartChargingLightTransfer({
          transferId,
          salesPartnerUuid,
        }),
        await masterdataEvsesCompatibility(transferId),
      ]);

      setTransferId(transferId);
      setCorrelationId(response[0].correlationId);
      setUiFlowPreferences({
        language: response[0].uiFlowPreferences.language,
        callbackUrlSuccess: response[0].uiFlowPreferences.callbackUrlSuccess,
        callbackUrlFailure: response[0].uiFlowPreferences.callbackUrlFailure,
      });
      setEvsesCompatibility(response[1]);

      const filteredEvsesList = response[1].filter(
        (evse) => evse.compatibilityStatus === "PREMIUM",
      );
      const manufacturersList = filteredEvsesList.map(
        (item) => item.manufacturer,
      );
      const manufacturersListDistinct = manufacturersList.filter(
        (manufacturer, index, list) => list.indexOf(manufacturer) === index,
      );

      setTimeout(() => {
        setIsLoading(false);
        setManufacturersList(manufacturersListDistinct);
      }, 1000);
    } catch (e) {
      setIsLoading(false);
      const error = e as Error;
      setMsg(error.msgId ?? "");
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    initialCall();
  }, [initialCall]);

  const handleSearchClick = () => {
    navigate(SMART_CHARGING_ROUTES.MANUFACTURER_SEARCH);
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const handleManufacturerClick = (manufacturer: string) => {
    return `${navigate(SMART_CHARGING_ROUTES.MODEL + `?manufacturer=${manufacturer}`)}`;
  };

  return (
    <Stack gap={5}>
      <Stack gap={5}>
        <Text
          text={t("page.wallboxCompatibility.pageTitle")}
          sx={(theme) => ({
            fontWeight: 500,
            fontSize: "32px",
            lineHeight: "40px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={t("page.wallboxCompatibility.pageSubTitle1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />
      </Stack>

      <Stack gap={5}>
        <Text
          text={t("page.wallboxCompatibility.formTitle")}
          sx={(theme) => ({
            fontSize: "24px",
            fontWeight: "500",
            lineHeight: "28px",
            fontFamily: "officina",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Box
          sx={(theme) => ({
            px: "16px",
            borderRadius: "12px",
            bgcolor: theme.palette.white.main,
          })}
        >
          <List>
            {isLoading && (
              <ListItem disablePadding disableGutters>
                <Skeleton
                  sx={{
                    fontSize: "3rem",
                    width: "100%",
                  }}
                />
              </ListItem>
            )}

            {manufacturersList &&
              manufacturersList.map((manufacturer, index) => (
                <EvseListItem
                  key={manufacturer}
                  itemText={manufacturer}
                  divider={index < manufacturersList.length - 1}
                  handleItemClick={() => handleManufacturerClick(manufacturer)}
                />
              ))}
          </List>
        </Box>
      </Stack>

      <Stack gap={2}>
        <Text
          text={t("page.wallboxCompatibility.para1")}
          sx={(theme) => ({
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "24px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Text
          text={
            <Trans
              i18nKey={
                t(
                  "page.wallboxCompatibility.para1Desc",
                ) as "page.wallboxCompatibility.para1Desc"
              }
              t={t}
              components={{ bold: <strong /> }}
            />
          }
          sx={(theme) => ({
            fontSize: "12px",
            fontWeight: "400",
            lineHeight: "20px",
            fontFamily: "Inter",
            color: theme.palette.mainFont.primary,
          })}
        />
      </Stack>

      <Stack>
        <PrimaryButton
          isLoading={isLoading}
          onClick={handleSearchClick}
          text={"Ich besitze eine andere Wallbox"}
          sx={{
            border: "1px solid #00000038",
            backgroundColor: "transparent",
            color: "#3B3B3B",
          }}
          disabled={msg !== ""}
        />
      </Stack>

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          severity="error"
          variant="filled"
          onClose={handleClose}
          sx={{ width: "100%" }}
        >
          {msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Compatibility;
